import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { Combobox } from "@/components/flexkit/combobox";
import { PasswordInput } from "@/components/podkit/forms/PasswordInput";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { SourceControlProviderIcon } from "@/components/runners/details/SourceControlProviderIcon";
import { TrackLocations } from "@/hooks/use-segment";
import { useIsUserAuthenticatedWithRunnerForHost, useListRunners, type PlainRunner } from "@/queries/runner-queries";
import {
    useCreateHostAuthenticationTokenForServiceAccount,
    useListRunnerSCMIntegrations,
} from "@/queries/runner-configuration-queries";
import { formatError } from "@/utils/errors";
import { RunnerKind, type SCMIntegration } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { useCallback, useEffect, useMemo, useState, type FC, type PropsWithChildren } from "react";
import { type PlainServiceAccount } from "@/queries/service-account-queries";
import { type ServiceAccountHeaders } from "@/api";
import { Text } from "@/components/podkit/typography/Text";
import { Heading2 } from "@/components/podkit/typography/Headings";
import { cn } from "@/components/podkit/lib/cn";
import IconCircleCheck from "central-icons/IconCircleCheck";
import { ErrorMessage } from "@/components/ErrorMessage";

type AddGitAuthenticationDialogProps = {
    open: boolean;
    onClose: () => void;
    serviceAccount: PlainServiceAccount;
    headers: ServiceAccountHeaders;
};

export const AddGitAuthenticationDialog: FC<AddGitAuthenticationDialogProps> = ({
    open,
    onClose,
    serviceAccount,
    headers,
}) => {
    const { toast } = useToast();

    const [selectedRunnerId, setSelectedRunnerId] = useState<string>("");
    const [selectedIntegrationId, setSelectedIntegrationId] = useState<string>("");
    const [pat, setPat] = useState<string>("");

    const readyToListRunners = open;
    const {
        data: runners,
        isPending: isLoadingRunners,
        error: listRunnersError,
    } = useListRunners({
        kind: RunnerKind.REMOTE,
        enabled: readyToListRunners,
        headers: headers,
    });

    const readyToListSCMIntegrations = !!selectedRunnerId;
    const {
        data: scmIntegrations,
        isLoading: isLoadingSCMIntegrations,
        error: listSCMIntegrationsError,
    } = useListRunnerSCMIntegrations(selectedRunnerId, {
        enabled: readyToListSCMIntegrations,
        headers: headers,
    });

    // For now we only support PAT-based based auth
    const patIntegrations = useMemo((): SCMIntegration[] => {
        return (scmIntegrations ?? []).filter((integration) => integration.pat);
    }, [scmIntegrations]);

    // Auto-select the first integration when integrations load
    useEffect(() => {
        if (patIntegrations.length > 0 && !selectedIntegrationId) {
            setSelectedIntegrationId(patIntegrations[0].id);
        }
    }, [patIntegrations, selectedIntegrationId]);

    const selectedIntegration = useMemo(() => {
        return patIntegrations.find((option) => option.id === selectedIntegrationId);
    }, [patIntegrations, selectedIntegrationId]);

    const readyToCheckAuth = !!selectedIntegration?.host;
    const {
        data: authData,
        isPending: isPendingAuthData,
        error: authDataError,
    } = useIsUserAuthenticatedWithRunnerForHost(selectedRunnerId, selectedIntegration?.host, {
        headers: headers,
        enabled: readyToCheckAuth,
    });

    const selectedRunner = useMemo(() => {
        return runners?.find((option) => option.runnerId === selectedRunnerId);
    }, [runners, selectedRunnerId]);

    const createHostAuthenticationToken = useCreateHostAuthenticationTokenForServiceAccount({ headers });

    const handleRunnerChange = useCallback((runnerId: string) => {
        setSelectedRunnerId(runnerId);
        // Reset integration selection when runner changes
        setSelectedIntegrationId("");
    }, []);

    const handleOpenChange = useCallback(
        (open: boolean) => {
            if (!open) {
                onClose();
                setSelectedRunnerId("");
                setSelectedIntegrationId("");
                setPat("");
            }
        },
        [onClose],
    );

    const handleSubmit = useCallback(async () => {
        const host = selectedIntegration?.host;
        const runnerId = selectedRunnerId;

        if (!host || !runnerId) {
            return;
        }

        try {
            await createHostAuthenticationToken.mutateAsync({ host, pat, runnerId });
            handleOpenChange(false);
        } catch (error) {
            toast({
                title: "Failed to add git authentication",
                description: formatError(error),
            });
        }
    }, [selectedIntegration?.host, selectedRunnerId, handleOpenChange, createHostAuthenticationToken, pat, toast]);

    const isFormValid = selectedRunnerId.length > 0 && selectedIntegrationId.length > 0 && pat.length > 0;

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent
                data-testid="add-git-authentication-dialog"
                className="max-w-md"
                data-track-location={TrackLocations.ServiceAccountAddGitAuthenticationDialog}
            >
                <DialogHeader>
                    <DialogTitle>Add git authentication</DialogTitle>
                    <DialogDescription>
                        Adding a git authentication allows{" "}
                        <span className="font-medium text-content-strong">{serviceAccount?.name}</span> access to
                        repositories and pull requests.
                    </DialogDescription>
                </DialogHeader>

                <DialogBody>
                    <div className="flex min-h-[220px] flex-col gap-4">
                        <ErrorMessage error={listRunnersError} />
                        <ErrorMessage error={listSCMIntegrationsError} />
                        <ErrorMessage error={authDataError} />
                        <ErrorMessage error={createHostAuthenticationToken.error} />
                        <div className="flex flex-col gap-2">
                            <label htmlFor="runner-select" className="text-sm font-medium text-content-primary">
                                Runner
                            </label>
                            <Combobox
                                value={selectedRunnerId}
                                onValueChange={handleRunnerChange}
                                loading={isLoadingRunners}
                                className="w-full"
                            >
                                <Combobox.Value>
                                    {selectedRunner ? (
                                        <Combobox.ValueLabel>{selectedRunner.name}</Combobox.ValueLabel>
                                    ) : (
                                        "Select runner"
                                    )}
                                </Combobox.Value>
                                {/* combobox-hack: Setting high z-index */}
                                <Combobox.Popover className="z-[999]" flip={false}>
                                    {!isLoadingRunners && !runners && (
                                        <Combobox.Empty>No runners available</Combobox.Empty>
                                    )}
                                    {isLoadingRunners && <Combobox.Loading>Loading runners...</Combobox.Loading>}
                                    <Combobox.List
                                        // combobox-hack: Setting max height to avoid overflow dialog
                                        className="max-h-[200px]"
                                        items={runners || []}
                                        searchKeys={["name"]}
                                        noMatchesComponent={<Combobox.Empty>No matching runners.</Combobox.Empty>}
                                    >
                                        {(option) => <RunnerOption key={option.runnerId} option={option} />}
                                    </Combobox.List>
                                </Combobox.Popover>
                            </Combobox>
                        </div>

                        <div className="flex flex-col gap-2">
                            <label htmlFor="integration-select" className="text-sm font-medium text-content-primary">
                                Git provider
                            </label>
                            <Combobox
                                value={selectedIntegrationId}
                                onValueChange={setSelectedIntegrationId}
                                loading={isLoadingSCMIntegrations}
                                disabled={!selectedRunnerId || isLoadingSCMIntegrations}
                                className="w-full"
                            >
                                <Combobox.Value>
                                    {selectedIntegration ? (
                                        <div className="flex items-center gap-2">
                                            <SourceControlProviderIcon scmId={selectedIntegration.scmId} size="sm" />
                                            <Combobox.ValueLabel>{selectedIntegration.host}</Combobox.ValueLabel>
                                        </div>
                                    ) : (
                                        "Select Git provider"
                                    )}
                                </Combobox.Value>
                                {/* combobox-hack: Setting high z-index */}
                                <Combobox.Popover className="z-[999] max-h-[150px]" flip={false}>
                                    {isLoadingSCMIntegrations && (
                                        <Combobox.Loading>Loading providers...</Combobox.Loading>
                                    )}
                                    {!selectedRunnerId && <Combobox.Empty>Select a runner first.</Combobox.Empty>}
                                    {!isLoadingSCMIntegrations && patIntegrations.length === 0 && (
                                        <Combobox.Empty>
                                            {selectedRunner?.name} doesn&apos;t have any PAT-enabled Git providers
                                            configured.
                                        </Combobox.Empty>
                                    )}
                                    <Combobox.List
                                        // combobox-hack: Setting max height to avoid overflow dialog
                                        className="max-h-[150px]"
                                        items={patIntegrations}
                                        searchKeys={["host", "displayName"]}
                                        noMatchesComponent={<Combobox.Empty>No matching integrations.</Combobox.Empty>}
                                    >
                                        {(option) => <IntegrationOption key={option.id} option={option} />}
                                    </Combobox.List>
                                </Combobox.Popover>
                            </Combobox>
                        </div>

                        <div
                            className={cn("transition-opacity", {
                                "pointer-events-none opacity-50": isPendingAuthData,
                            })}
                        >
                            <div className="flex flex-col gap-4">
                                <div className="self-stretch border-b border-border-base" aria-hidden="true" />

                                {authData && authData.type === "Authenticated" && (
                                    <AlreadyAuthenticated
                                        serviceAccount={serviceAccount}
                                        selectedRunner={selectedRunner}
                                        selectedIntegration={selectedIntegration}
                                    />
                                )}

                                {isPendingAuthData || authData?.type !== "Authenticated" ? (
                                    <>
                                        <Text>
                                            All authentication happens on your infrastructure, ensuring maximum security
                                            and control.
                                        </Text>

                                        <Numbered number={1} heading="Create a Personal Access Token">
                                            <Text>
                                                Enable read/write access to repository content.&nbsp;
                                                <a
                                                    href={
                                                        authData?.type === "AuthenticationRequired"
                                                            ? authData?.patAuth?.docsUrl
                                                            : "#"
                                                    }
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="text-content-link"
                                                >
                                                    Learn more
                                                </a>
                                                .
                                            </Text>

                                            <Text>
                                                Go to {selectedIntegration?.host} &gt; Settings &gt;{" "}
                                                <a
                                                    href={
                                                        authData?.type === "AuthenticationRequired"
                                                            ? authData?.patAuth?.createUrl
                                                            : "#"
                                                    }
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="text-content-link"
                                                >
                                                    Developer Settings
                                                </a>
                                            </Text>
                                        </Numbered>

                                        <Numbered number={2} heading="Paste your Personal Access Token">
                                            <Text>Tokens are encrypted. You can manage them in settings later.</Text>
                                            <PasswordInput
                                                value={pat}
                                                disabled={isPendingAuthData}
                                                onChange={(e) => setPat(e.target.value)}
                                                placeholder={
                                                    authData?.type === "AuthenticationRequired"
                                                        ? authData?.patAuth?.example
                                                        : "pat_10987654321abcdefghijklmnop"
                                                }
                                                revealable={true}
                                                data-testid="add-git-authentication-dialog-pat"
                                                autoComplete="off"
                                                aria-autocomplete="none"
                                                data-1p-ignore
                                                data-lpignore
                                                data-protonpass-ignore
                                            />
                                        </Numbered>
                                    </>
                                ) : undefined}
                            </div>
                        </div>
                    </div>
                </DialogBody>

                <DialogFooter className="flex items-center justify-end">
                    <DialogClose asChild>
                        <Button variant="secondary" data-testid="add-git-authentication-dialog-cancel">
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        variant="primary"
                        disabled={!isFormValid || createHostAuthenticationToken.isPending}
                        onClick={handleSubmit}
                        data-testid="add-git-authentication-dialog-add"
                        data-tracking-id="add-git-authentication-dialog-add"
                    >
                        Add
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const AlreadyAuthenticated: FC<{
    serviceAccount?: PlainServiceAccount;
    selectedRunner?: PlainRunner;
    selectedIntegration?: SCMIntegration;
}> = ({ serviceAccount, selectedRunner, selectedIntegration }) => {
    return (
        <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-2">
                <IconCircleCheck size={24} className="text-content-success" />
                <Text className="text-lg font-medium">Connected</Text>
            </div>
            <Text className="text-sm text-content-strong">
                <span className="font-medium text-content-strong">{serviceAccount?.name}</span> is already authenticated
                with <span className="font-medium text-content-strong">{selectedRunner?.name}</span> for{" "}
                <span className="font-medium text-content-strong">{selectedIntegration?.host}</span>.
            </Text>
        </div>
    );
};

const RunnerOption: FC<{ option: PlainRunner }> = ({ option }) => {
    return (
        <Combobox.ListItem value={option.runnerId}>
            <Combobox.ListItemTitle className="text-base">{option.name}</Combobox.ListItemTitle>
        </Combobox.ListItem>
    );
};

const IntegrationOption: FC<{ option: SCMIntegration }> = ({ option }) => {
    return (
        <Combobox.ListItem value={option.id}>
            <div className="flex items-center gap-2">
                <SourceControlProviderIcon scmId={option.scmId} size="sm" />
                <Combobox.ListItemTitle className="text-base">{option.host}</Combobox.ListItemTitle>
            </div>
        </Combobox.ListItem>
    );
};

export const Numbered: FC<PropsWithChildren & { number: number; heading: string }> = ({
    number,
    heading,
    children,
}) => {
    return (
        <div className="flex flex-row justify-items-start gap-2">
            <span className="text-base font-medium text-content-strong">{number}</span>
            <div className="flex flex-col gap-1">
                <Heading2 className="text-base font-medium text-content-primary">{heading}</Heading2>
                {children}
            </div>
        </div>
    );
};
